import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { servicesAPI, importAPI } from '../../api'
import { useForm } from 'react-hook-form'
import { RiAddLine, RiEditLine, RiDeleteBinLine, RiLoader4Line, RiCloseLine, RiTimeLine, RiPriceTag3Line } from 'react-icons/ri'
import toast from 'react-hot-toast'
import CsvImportButton from '../../components/admin/CsvImportButton'

function ServiceModal({ service, categories, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!service

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    defaultValues: service || { name: '', description: '', price: '', duration_minutes: 60, category_id: '' },
  })

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? servicesAPI.update(service.id, data) : servicesAPI.create(data),
    onSuccess: () => { qc.invalidateQueries(['admin-services']); toast.success(isEdit ? 'Service updated' : 'Service created'); onClose() },
    onError: () => toast.error('Operation failed'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-salon-lg w-full max-w-md p-7 z-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-salon-dark text-xl font-bold">{isEdit ? 'Edit Service' : 'Add Service'}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-salon-cream"><RiCloseLine className="text-xl text-salon-muted" /></button>
        </div>

        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div>
            <label className="input-label">Service Name *</label>
            <input {...register('name', { required: true })} className="input-field" placeholder="e.g. Hair Coloring" />
          </div>
          <div>
            <label className="input-label">Category</label>
            <select {...register('category_id')} className="input-field">
              <option value="">Select category</option>
              {(categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">Price (₹) *</label>
              <input {...register('price', { required: true, min: 0 })} type="number" className="input-field" placeholder="1500" />
            </div>
            <div>
              <label className="input-label">Duration (min) *</label>
              <input {...register('duration_minutes', { required: true, min: 15 })} type="number" className="input-field" placeholder="60" />
            </div>
          </div>
          <div>
            <label className="input-label">Description</label>
            <textarea {...register('description')} className="input-field min-h-[80px] resize-none" placeholder="Brief description..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? <RiLoader4Line className="animate-spin" /> : isEdit ? 'Save' : 'Create Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function ManageServices() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(null)

  const { data: services, isLoading } = useQuery({
    queryKey: ['admin-services'],
    queryFn: () => servicesAPI.getAll(),
    select: (r) => r.data?.data || [],
  })
  const { data: categories } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => servicesAPI.getCategories(),
    select: (r) => r.data?.data || [],
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => servicesAPI.delete(id),
    onSuccess: () => { qc.invalidateQueries(['admin-services']); toast.success('Service deleted') },
    onError: () => toast.error('Delete failed'),
  })

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-salon-dark text-2xl font-bold">Services</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvImportButton
            label="Import services"
            uploader={(file) => importAPI.services(file)}
            templateUrl="/templates/services.csv"
            templateHeaders="category, name, duration, price, description, deposit_amount, is_active"
            invalidateKeys={[['admin-services'], ['services']]}
          />
          <button onClick={() => setModal('add')} className="btn-primary text-sm"><RiAddLine /> Add Service</button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><RiLoader4Line className="animate-spin text-3xl text-salon-primary" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-salon-cream-dark overflow-hidden">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-salon-cream text-salon-muted text-xs font-semibold uppercase tracking-wider">
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Category</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Duration</th>
                <th className="text-left px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-salon-cream-dark">
              {(services || []).map((s) => (
                <tr key={s.id} className="hover:bg-salon-cream/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-salon-dark">{s.name}</p>
                    <p className="text-xs text-salon-muted mt-0.5 max-w-[200px] truncate">{s.description}</p>
                  </td>
                  <td className="px-5 py-3.5 text-salon-muted">{s.category?.name || '–'}</td>
                  <td className="px-5 py-3.5">
                    <span className="flex items-center gap-1 text-salon-secondary font-semibold">
                      <RiPriceTag3Line className="text-sm" />₹{parseFloat(s.price).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="flex items-center gap-1 text-salon-muted">
                      <RiTimeLine className="text-sm" />{s.duration_minutes} min
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-1.5">
                      <button onClick={() => setModal(s)}
                        className="p-1.5 rounded-lg bg-salon-cream text-salon-primary hover:bg-salon-primary/10 transition-colors">
                        <RiEditLine />
                      </button>
                      <button onClick={() => window.confirm('Delete service?') && deleteMutation.mutate(s.id)}
                        className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                        <RiDeleteBinLine />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ServiceModal service={modal === 'add' ? null : modal} categories={categories} onClose={() => setModal(null)} />
      )}
    </div>
  )
}
